<script setup lang="ts">
    import {onMounted, ref, watch, computed, nextTick } from "vue";
    import { useRothcoBreakpoints } from "@/helpers/composables/useBreakpoints";
    import { useMutationObserver } from "@vueuse/core";
    import { useYotpoStore } from "@/store/yotpo";
    import { storeToRefs } from "pinia";

    let props = defineProps({
        instance_id: String,
        widgetProps: Object,
        html: String
    })

    const yotpoRef = ref(null);

    const yotpoStore = useYotpoStore();
    const { initYotpoWidget } = yotpoStore;
    const { yotpoReady } = storeToRefs(yotpoStore);

    const breakpoints = useRothcoBreakpoints();
    const currentBreakPoint = computed(()=>{
        if(breakpoints.greaterOrEqual('desktop').value) return 'desktop';
        else if(breakpoints.greaterOrEqual('tablet').value) return 'tablet';
        else return 'mobile';
    })

    const applyClasses = async (breakpoint: string) => {
        await nextTick(()=>{
            const el = yotpoRef.value?.querySelector(':first-child');
            
            el?.classList.toggle("yotpo-device-desktop", breakpoint === 'desktop' || breakpoint === 'tablet');
            el?.classList.toggle("yotpo-device-mobile", breakpoint === 'mobile');

            el?.classList.toggle("yotpo-display-l", breakpoint === 'desktop');
            el?.classList.toggle("yotpo-display-m", breakpoint === 'tablet');
            el?.classList.toggle("yotpo-display-s", breakpoint === 'mobile');


            // if el contains style visibility: hidden, set display: none
            if(el?.style?.visibility === 'hidden'){
                el.style.display = 'none';
            }
        })
    };

    const isMounted = ref(false);
    onMounted( ()=>{
        isMounted.value = true;
        nextTick(()=>{
            const { stop } = useMutationObserver(
                yotpoRef.value,
                () => {
                    applyClasses(currentBreakPoint.value);
                    stop();
                },
                { childList: true, subtree: true }
            );

            watch(()=>(yotpoReady.value), (newVal)=>{
                if(newVal) {
                    initYotpoWidget(props.instance_id, yotpoRef);
                }
            }, { immediate: true });

            watch(()=>(currentBreakPoint.value), applyClasses, { immediate: true });
        })
    })

    const yotpoProps = computed(()=>{
        if (!props.widgetProps) return {};
        return Object.fromEntries(
            Object.entries(props.widgetProps).map(([key, value]) => [
                    `data-yotpo-${key}`,
                    value
                ])
            )
        })
    
</script>
<template>
     <div
        v-if="isMounted"
        ref="yotpoRef"
        class="yotpo-widget-instance" 
        :data-yotpo-instance-id="props.instance_id"
        v-bind="yotpoProps"
        v-html="props.html">
    </div>
</template>